import React from 'react';
import { createBoardTiles } from '../boardConfig';
import { DEFAULT_BAG_PRESET, GAME_CONFIG } from '../config/gameConfig';
import { generateId } from '../../../utils/generateId';
import { normalizeRunSettings } from '../config/runSettings';
import { evaluateMove } from '../engine/moveEvaluation';
import { normalizeActiveGameEffects } from '../engine/scoringEffects';
import { getRoundTarget, getRoundTileTarget } from '../engine/shop';
import { createInitialBag, createTilesFromLegacyLetters, drawTiles } from '../engine/tileBag';
import {
  clearStoredGameState,
  flushStoredGameState,
  getStoredGameState,
  getStoredPlayerState,
  STORAGE_QUOTA_EXCEEDED_EVENT_NAME,
  writeStoredGameState,
  writeStoredPlayerState,
} from '../services/localStorageService';
import { trackAbandonRun, trackRunSessionStart } from '../services/playerStatsTrackingService';
import {
  getRoundDiscardsWithEffects,
  getRoundSubmitsWithEffects,
  getShopRerollsWithEffects,
} from './scrabbleGameHelpers';
import { useRoundLoop } from './useRoundLoop';
import { useShopLoop } from './useShopLoop';
import { useScrabbleWordValidation } from './useScrabbleWordValidation';
import { useShopRevealAudio } from './useShopRevealAudio';

const normalizeTile = (tile, fallbackId) => ({
  id: tile?.id || fallbackId,
  letter: String(tile?.letter || '').toUpperCase(),
  modifiers: tile?.modifiers && typeof tile.modifiers === 'object' && !Array.isArray(tile.modifiers)
    ? { ...tile.modifiers, extras: Array.isArray(tile.modifiers.extras) ? [...tile.modifiers.extras] : [] }
    : { score: 0, multiplier: 1, extras: [] },
});

const normalizeTileCollection = (tiles, idPrefix = 'tile') =>
  (Array.isArray(tiles) ? tiles : []).map((tile, index) =>
    normalizeTile(tile, `${idPrefix}-${index + 1}`)
  );

const normalizeBoardTileMap = (board, idPrefix = 'board') => {
  if (!board || typeof board !== 'object') {
    return {};
  }

  return Object.entries(board).reduce((acc, [squareTileId, tile], index) => {
    acc[squareTileId] = normalizeTile(tile, `${idPrefix}-${index + 1}`);
    return acc;
  }, {});
};

const getMinimumRunPointsForRoundIndex = (roundIndex = 1) => {
  const normalizedRoundIndex = Math.max(1, Number(roundIndex) || 1);
  let minimum = 0;

  for (let index = 1; index < normalizedRoundIndex; index += 1) {
    minimum += getRoundTarget(index);
  }

  return minimum;
};

const createInitialState = ({ modifierOverrides, bagPreset = DEFAULT_BAG_PRESET }) => {
  const runSettings = normalizeRunSettings();
  const bag = createInitialBag(bagPreset);
  const firstDraw = drawTiles(bag, GAME_CONFIG.rackSize);
  const roundIndex = 1;
  const activeGameEffects = [];

  return {
    runId: generateId(),
    boardTiles: createBoardTiles(modifierOverrides),
    boardModifierOverrides: {},
    heldBoardModifiers: [],
    ownedGameModifiers: [],
    committedBoard: {},
    stagedBoard: {},
    bagTiles: firstDraw.bag,
    rackTiles: firstDraw.drawn,
    runPoints: 0,
    score: 0,
    history: [],
    tilesPlacedThisRound: 0,
    activeGameEffects,
    runSettings,
    gamePhase: 'playing',
    roundIndex,
    roundTarget: getRoundTarget(roundIndex),
    roundTileTarget: getRoundTileTarget(roundIndex),
    submitsRemaining: getRoundSubmitsWithEffects(runSettings, activeGameEffects),
    discardsRemaining: getRoundDiscardsWithEffects(runSettings, activeGameEffects),
    isDiscardSelecting: false,
    discardSelectionTileIds: [],
    shopPicksRemaining: 0,
    shopRerollsRemaining: 0,
    shopOptions: [],
    selectedShopOptionId: null,
    pendingBoardPlacement: null,
    infiniteMode: false,
  };
};

const hydrateState = ({ stored, modifierOverrides, bagPreset = DEFAULT_BAG_PRESET }) => {
  const initial = createInitialState({ modifierOverrides, bagPreset });

  if (!stored || typeof stored !== 'object') {
    return initial;
  }

  const storedBagTiles = normalizeTileCollection(stored.bagTiles, 'bag');
  const storedRackTiles = normalizeTileCollection(stored.rackTiles, 'rack');

  const legacyBagLetters = Array.isArray(stored.bagLetters) ? stored.bagLetters : null;
  const legacyRackLetters = Array.isArray(stored.rackLetters) ? stored.rackLetters : null;

  let legacyCounter = 0;
  const legacyIdFactory = () => {
    legacyCounter += 1;
    return `legacy-${legacyCounter}`;
  };

  const migratedBagTiles = legacyBagLetters
    ? createTilesFromLegacyLetters(legacyBagLetters, legacyIdFactory)
    : [];
  const migratedRackTiles = legacyRackLetters
    ? createTilesFromLegacyLetters(legacyRackLetters, legacyIdFactory)
    : [];
  const runSettings = normalizeRunSettings(stored.runSettings);
  const activeGameEffects = normalizeActiveGameEffects(stored.activeGameEffects);
  const hydratedRoundIndex = Math.max(1, Number(stored.roundIndex) || 1);
  const hasStoredRunPoints = Number.isFinite(Number(stored.runPoints));
  const fallbackRunPoints = getMinimumRunPointsForRoundIndex(hydratedRoundIndex);

  const storedBoardModifierOverrides =
    stored.boardModifierOverrides && typeof stored.boardModifierOverrides === 'object' && !Array.isArray(stored.boardModifierOverrides)
      ? stored.boardModifierOverrides
      : {};

  const storedHeldBoardModifiers = Array.isArray(stored.heldBoardModifiers)
    ? stored.heldBoardModifiers
    : [];
  const storedOwnedGameModifiers = Array.isArray(stored.ownedGameModifiers)
    ? stored.ownedGameModifiers
    : [];

  return {
    ...initial,
    runId: stored.runId || generateId(),
    boardTiles: createBoardTiles({ ...modifierOverrides, ...storedBoardModifierOverrides }),
    boardModifierOverrides: storedBoardModifierOverrides,
    heldBoardModifiers: storedHeldBoardModifiers,
    ownedGameModifiers: storedOwnedGameModifiers,
    committedBoard: normalizeBoardTileMap(stored.committedBoard, 'committed'),
    stagedBoard: normalizeBoardTileMap(stored.stagedBoard, 'staged'),
    bagTiles:
      storedBagTiles.length || storedRackTiles.length
        ? storedBagTiles
        : migratedBagTiles.length
          ? migratedBagTiles
          : initial.bagTiles,
    rackTiles:
      storedBagTiles.length || storedRackTiles.length
        ? storedRackTiles
        : migratedRackTiles.length
          ? migratedRackTiles
          : initial.rackTiles,
    runPoints: Math.max(
      0,
      hasStoredRunPoints ? Number(stored.runPoints) : fallbackRunPoints
    ),
    score: Number(stored.score || 0),
    history: Array.isArray(stored.history) ? stored.history : [],
    activeGameEffects,
    runSettings,
    gamePhase:
      stored.gamePhase === 'shop'
        ? 'shop'
        : stored.gamePhase === 'round-complete'
          ? 'round-complete'
          : stored.gamePhase === 'game-over'
            ? 'game-over'
            : stored.gamePhase === 'victory'
              ? 'victory'
              : 'playing',
    infiniteMode: Boolean(stored.infiniteMode),
    roundIndex: hydratedRoundIndex,
    roundTarget: Math.max(1, Number(stored.roundTarget) || getRoundTarget(stored.roundIndex || 1)),
    roundTileTarget: Math.max(0, Number(stored.roundTileTarget) || getRoundTileTarget(stored.roundIndex || 1)),
    tilesPlacedThisRound: Math.max(0, Number(stored.tilesPlacedThisRound) || 0),
    submitsRemaining: Math.max(
      0,
      Number(stored.submitsRemaining ?? getRoundSubmitsWithEffects(runSettings, activeGameEffects))
    ),
    discardsRemaining: Math.max(
      0,
      Number(stored.discardsRemaining ?? getRoundDiscardsWithEffects(runSettings, activeGameEffects))
    ),
    isDiscardSelecting: Boolean(stored.isDiscardSelecting),
    discardSelectionTileIds: Array.isArray(stored.discardSelectionTileIds)
      ? stored.discardSelectionTileIds.filter(Boolean)
      : [],
    shopPicksRemaining:
      stored.gamePhase === 'shop'
        ? Math.max(1, Number(stored.shopPicksRemaining) || 1)
        : Math.max(0, Number(stored.shopPicksRemaining) || 0),
    shopRerollsRemaining:
      stored.gamePhase === 'shop'
        ? Math.max(
            0,
            Number(stored.shopRerollsRemaining ?? getShopRerollsWithEffects(runSettings, activeGameEffects))
          )
        : Math.max(0, Number(stored.shopRerollsRemaining) || 0),
    shopOptions: Array.isArray(stored.shopOptions) ? stored.shopOptions : [],
    selectedShopOptionId: stored.selectedShopOptionId || null,
  };
};

export const useScrabbleGame = ({ modifierOverrides = {}, tileModifierOverrides = {} }) => {
  const initialLoadedRef = React.useRef(false);
  const [state, setState] = React.useState(() =>
    createInitialState({ modifierOverrides, bagPreset: DEFAULT_BAG_PRESET })
  );
  const [playerName, setPlayerName] = React.useState(GAME_CONFIG.player.defaultName);
  const [submitMessage, setSubmitMessage] = React.useState('');

  useShopRevealAudio({ gamePhase: state.gamePhase, shopOptions: state.shopOptions });

  React.useEffect(() => {
    if (initialLoadedRef.current) {
      return;
    }

    initialLoadedRef.current = true;

    const storedGameState = getStoredGameState();
    const storedPlayerState = getStoredPlayerState();
    const selectedBagPreset = storedPlayerState?.bagPreset || DEFAULT_BAG_PRESET;

    const hydrated = hydrateState({ stored: storedGameState, modifierOverrides, bagPreset: selectedBagPreset });
    setState(hydrated);

    if (storedPlayerState?.name) {
      setPlayerName(storedPlayerState.name);
    }

    trackRunSessionStart({ storedGameState, selectedBagPreset, runId: hydrated.runId });
  }, [modifierOverrides]);

  React.useEffect(() => {
    const previousPlayerState = getStoredPlayerState() || {};
    writeStoredPlayerState({ ...previousPlayerState, name: playerName });
  }, [playerName]);

  const evaluation = React.useMemo(
    () =>
      evaluateMove({
        committedBoard: state.committedBoard,
        stagedBoard: state.stagedBoard,
        boardTiles: state.boardTiles,
        tileModifierOverrides,
        activeGameEffects: state.activeGameEffects,
      }),
    [
      state.committedBoard,
      state.stagedBoard,
      state.boardTiles,
      state.activeGameEffects,
      tileModifierOverrides,
    ]
  );

  const {
    dictionaryStatus,
    dictionaryError,
    wordsWithValidation,
    tileValidationState,
    isDictionaryValid,
    resetWordValidationState,
  } = useScrabbleWordValidation({ evaluation });

  const canSubmit =
    state.gamePhase === 'playing' &&
    state.submitsRemaining > 0 &&
    !state.isDiscardSelecting &&
    evaluation.hasTiles &&
    evaluation.isStructurallyValid &&
    dictionaryStatus === 'ready' &&
    isDictionaryValid &&
    !dictionaryError;

  const roundLoop = useRoundLoop({
    state,
    setState,
    evaluation,
    wordsWithValidation,
    dictionaryStatus,
    canSubmit,
    setSubmitMessage,
  });

  const shopLoop = useShopLoop({ setState, setSubmitMessage });

  React.useEffect(() => {
    if (state.gamePhase === 'game-over') {
      clearStoredGameState();
    }
  }, [state.gamePhase]);

  React.useEffect(() => {
    if (state.gamePhase === 'game-over') return;
    writeStoredGameState({
      runId: state.runId,
      boardModifierOverrides: state.boardModifierOverrides,
      heldBoardModifiers: state.heldBoardModifiers,
      ownedGameModifiers: state.ownedGameModifiers,
      committedBoard: state.committedBoard,
      stagedBoard: state.stagedBoard,
      rackTiles: state.rackTiles,
      bagTiles: state.bagTiles,
      runPoints: state.runPoints,
      score: state.score,
      history: state.history,
      activeGameEffects: state.activeGameEffects,
      runSettings: state.runSettings,
      gamePhase: state.gamePhase,
      roundIndex: state.roundIndex,
      roundTarget: state.roundTarget,
      roundTileTarget: state.roundTileTarget,
      tilesPlacedThisRound: state.tilesPlacedThisRound,
      submitsRemaining: state.submitsRemaining,
      discardsRemaining: state.discardsRemaining,
      isDiscardSelecting: state.isDiscardSelecting,
      discardSelectionTileIds: state.discardSelectionTileIds,
      shopPicksRemaining: state.shopPicksRemaining,
      shopRerollsRemaining: state.shopRerollsRemaining,
      shopOptions: state.shopOptions,
      selectedShopOptionId: state.selectedShopOptionId,
      infiniteMode: state.infiniteMode,
    });
  }, [state]);

  React.useEffect(() => () => {
    flushStoredGameState();
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleQuotaExceeded = () => {
      setSubmitMessage('Storage full — game progress may not be saved. Clear browser data to fix.');
    };

    window.addEventListener(STORAGE_QUOTA_EXCEEDED_EVENT_NAME, handleQuotaExceeded);
    return () => window.removeEventListener(STORAGE_QUOTA_EXCEEDED_EVENT_NAME, handleQuotaExceeded);
  }, []);

  const playAgain = React.useCallback(() => {
    const storedPlayerState = getStoredPlayerState();
    const selectedBagPreset = storedPlayerState?.bagPreset || DEFAULT_BAG_PRESET;
    const newState = createInitialState({ modifierOverrides, bagPreset: selectedBagPreset });

    clearStoredGameState();
    setState(newState);
    resetWordValidationState();
    setSubmitMessage('New game started.');
    trackRunSessionStart({ storedGameState: null, selectedBagPreset, runId: newState.runId });
  }, [modifierOverrides, resetWordValidationState]);

  const clearGameState = React.useCallback(() => {
    clearStoredGameState();
  }, []);

  const forceEndRun = React.useCallback(() => {
    let forceApplied = false;
    let capturedRunData = null;

    setState((prev) => {
      if (prev.gamePhase === 'game-over') return prev;
      forceApplied = true;
      capturedRunData = { roundIndex: prev.roundIndex, runPoints: prev.runPoints };
      return { ...prev, gamePhase: 'game-over' };
    });

    if (forceApplied && capturedRunData) {
      trackAbandonRun(capturedRunData);
      clearStoredGameState();
    }
  }, []);

  const continueInfiniteMode = React.useCallback(() => {
    setState((prev) => {
      if (prev.gamePhase !== 'victory') {
        return prev;
      }

      return { ...prev, gamePhase: 'round-complete', infiniteMode: true };
    });
  }, []);

  return {
    playerName,
    setPlayerName,
    boardTiles: state.boardTiles,
    boardModifierOverrides: state.boardModifierOverrides,
    heldBoardModifiers: state.heldBoardModifiers,
    ownedGameModifiers: state.ownedGameModifiers,
    pendingBoardPlacement: state.pendingBoardPlacement,
    committedBoard: state.committedBoard,
    stagedBoard: state.stagedBoard,
    bagTiles: state.bagTiles,
    rackTiles: state.rackTiles,
    score: state.score,
    history: state.history,
    activeGameEffects: state.activeGameEffects,
    gamePhase: state.gamePhase,
    submitsRemaining: state.submitsRemaining,
    discardsRemaining: state.discardsRemaining,
    isDiscardSelecting: state.isDiscardSelecting,
    discardSelectionTileIds: state.discardSelectionTileIds,
    roundIndex: state.roundIndex,
    shopPicksRemaining: state.shopPicksRemaining,
    shopRerollsRemaining: state.shopRerollsRemaining,
    shopOptions: state.shopOptions,
    selectedShopOptionId: state.selectedShopOptionId,
    scorePreview: evaluation.scorePreview,
    words: wordsWithValidation,
    tileValidationState,
    dictionaryStatus,
    dictionaryError,
    canSubmit,
    submitMessage,
    setSubmitMessage,
    structureErrors: evaluation.errors,
    isGameOver: state.gamePhase === 'game-over',
    isVictory: state.gamePhase === 'victory',
    runPoints: state.runPoints,
    targetScore: state.roundTarget,
    roundTileTarget: state.roundTileTarget,
    tilesPlacedThisRound: state.tilesPlacedThisRound,
    maxSubmitsPerRound: getRoundSubmitsWithEffects(state.runSettings, state.activeGameEffects),
    maxDiscardsPerRound: getRoundDiscardsWithEffects(state.runSettings, state.activeGameEffects),
    ...roundLoop,
    ...shopLoop,
    playAgain,
    clearGameState,
    continueInfiniteMode,
    forceEndRun,
  };
};
